import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { fetchMicroLearningFilters } from '../../../api/api'
import { MicroLearningFilterOptions } from '../../../api/models'
import { FormFieldLabel } from '../../../components/form/FormFieldLabel'
import styles from './FiltersDialog.module.css'

interface FiltersDialogProps {
  open: boolean
  onClose: () => void
  onApply: (filters: {
    ou: string
    area: string
    glaStrategicPriority: string
    scalePotential: '' | 'true' | 'false'
  }) => void
  initialFilters: {
    ou: string
    area: string
    glaStrategicPriority: string
    scalePotential: '' | 'true' | 'false'
  }
}

const EMPTY_FILTERS = {
  ou: '',
  area: '',
  glaStrategicPriority: '',
  scalePotential: '' as '' | 'true' | 'false',
}

const FiltersDialog = ({ open, onClose, onApply, initialFilters }: FiltersDialogProps) => {
  const [pendingFilters, setPendingFilters] = useState(initialFilters)
  const [filterOptions, setFilterOptions] = useState<MicroLearningFilterOptions | null>(null)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (open) {
      setPendingFilters(initialFilters)
      setFetchError(false)
      setLoading(true)

      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller

      fetchMicroLearningFilters()
        .then(options => {
          if (controller.signal.aborted) return
          if (!options) {
            setFetchError(true)
            return
          }
          setFilterOptions(options)
          setPendingFilters(prev => ({
            ou: options.ou.includes(prev.ou) ? prev.ou : '',
            area: options.area.includes(prev.area) ? prev.area : '',
            glaStrategicPriority: options.gla_strategic_priority.includes(prev.glaStrategicPriority)
              ? prev.glaStrategicPriority
              : '',
            scalePotential: prev.scalePotential,
          }))
        })
        .catch(() => {
          if (!controller.signal.aborted) setFetchError(true)
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false)
        })
    }

    return () => {
      abortControllerRef.current?.abort()
    }
  }, [open, initialFilters])

  const handleClear = () => {
    onApply(EMPTY_FILTERS)
  }

  const handleApply = () => {
    onApply(pendingFilters)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, pb: 1 }}>
        Filters
        <Button
          variant="text"
          size="small"
          onClick={handleClear}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            textTransform: 'none',
            color: 'rgba(244,0,9,1)',
            fontWeight: 600,
          }}>
          Clear all
        </Button>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
        <div>
          <FormFieldLabel text="Scale Potential" />
          <div className={styles.chipOptionsList}>
            <Chip
              label="Show All"
              className={pendingFilters.scalePotential === '' ? styles.chipOptionSelected : styles.chipOption}
              onClick={() => setPendingFilters(prev => ({ ...prev, scalePotential: '' }))}
            />
            <Chip
              label="Yes"
              className={pendingFilters.scalePotential === 'true' ? styles.chipOptionSelected : styles.chipOption}
              onClick={() => setPendingFilters(prev => ({ ...prev, scalePotential: 'true' }))}
            />
            <Chip
              label="No"
              className={pendingFilters.scalePotential === 'false' ? styles.chipOptionSelected : styles.chipOption}
              onClick={() => setPendingFilters(prev => ({ ...prev, scalePotential: 'false' }))}
            />
          </div>
        </div>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : fetchError ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Unable to load filter options. Please try again later.
          </Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            {filterOptions && filterOptions.ou.length > 0 && (
              <FormControl size="small" fullWidth>
                <FormLabel sx={{ fontSize: 13, mb: 0.5 }}>Operation Unit</FormLabel>
                <Select
                  value={pendingFilters.ou}
                  displayEmpty
                  onChange={e => setPendingFilters(prev => ({ ...prev, ou: e.target.value }))}>
                  <MenuItem value="">
                    <em style={{ color: '#aaa', fontStyle: 'normal' }}>All</em>
                  </MenuItem>
                  {filterOptions.ou.map(v => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {filterOptions && filterOptions.area.length > 0 && (
              <FormControl size="small" fullWidth>
                <FormLabel sx={{ fontSize: 13, mb: 0.5 }}>Area</FormLabel>
                <Select
                  value={pendingFilters.area}
                  displayEmpty
                  onChange={e => setPendingFilters(prev => ({ ...prev, area: e.target.value }))}>
                  <MenuItem value="">
                    <em style={{ color: '#aaa', fontStyle: 'normal' }}>All</em>
                  </MenuItem>
                  {filterOptions.area.map(v => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {filterOptions && filterOptions.gla_strategic_priority.length > 0 && (
              <FormControl size="small" fullWidth>
                <FormLabel sx={{ fontSize: 13, mb: 0.5 }}>GLA Strategic Priority</FormLabel>
                <Select
                  value={pendingFilters.glaStrategicPriority}
                  displayEmpty
                  onChange={e =>
                    setPendingFilters(prev => ({ ...prev, glaStrategicPriority: e.target.value }))
                  }>
                  <MenuItem value="">
                    <em style={{ color: '#aaa', fontStyle: 'normal' }}>All</em>
                  </MenuItem>
                  {filterOptions.gla_strategic_priority.map(v => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {filterOptions &&
              filterOptions.ou.length === 0 &&
              filterOptions.area.length === 0 &&
              filterOptions.gla_strategic_priority.length === 0 && (
                <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', color: 'text.secondary', py: 2 }}>
                  No filter options available
                </Box>
              )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderColor: 'rgba(244,0,9,1)',
            color: 'rgba(244,0,9,1)',
            fontWeight: 600,
            height: 40,
          }}>
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleApply}
          disabled={loading}
          sx={{
            textTransform: 'none',
            backgroundColor: 'rgba(244,0,9,1)',
            fontWeight: 600,
            height: 40,
            '&:hover': { backgroundColor: 'rgba(200,0,0,1)' },
          }}>
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FiltersDialog
